'use client';

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '@/components/layout/Layout';
import DefaultPage from '@/components/templates/DefaultPage';
import { pageAPI, postAPI } from '@/lib/api';
import Link from 'next/link';

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content: string;
  featuredImage?: string;
  status: string;
  categoryId?: string;
  publishedAt?: string;
  createdAt: string;
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  author?: {
    username: string;
  };
  viewCount?: number;
}

export default function NewsPage() {
  const { data: pageData, isLoading: pageLoading } = useQuery({
    queryKey: ['page', 'news'],
    queryFn: () => pageAPI.getBySlug('news'),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['post-categories'],
    queryFn: () => postAPI.getCategories(),
  });

  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ['posts', { status: 'published', limit: 50 }],
    queryFn: () => postAPI.getList({
      status: 'published',
      limit: 50,
    }),
  });

  const categories = categoriesData?.data || [];
  const newsCategory = categories.find((cat: any) => cat.slug === 'news' || cat.name === '新闻');
  const newsCategoryId = newsCategory?.id;

  const allPosts = postsData?.data || [];
  const publishedPosts = allPosts.filter((post: Post) => {
    if (post.status !== 'published') return false;
    if (!newsCategoryId) return true;
    return post.categoryId === newsCategoryId || post.category?.id === newsCategoryId;
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const stripHtml = (html: string, length?: number) => {
    if (!html) return '';
    const text = html.replace(/<[^>]*>/g, '');
    if (length && text.length > length) {
      return text.substring(0, length) + '...';
    }
    return text;
  };

  return (
    <Layout>
      <DefaultPage
        title={pageData?.data?.headerTitle || "新闻中心"}
        subtitle={pageData?.data?.headerSubtitle || "了解我们的最新动态和行业资讯"}
        layout="two-column"
        leftColumn={
          <div className="elementor-widget-wrap">
            {/* 显示文章列表 */}
            {postsLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">加载中...</p>
              </div>
            ) : publishedPosts.length > 0 ? (
              <div className="news-list">
                {publishedPosts.map((post: Post) => (
                  <article key={post.id} className="news-item" style={{ 
                    marginBottom: '24px', 
                    paddingBottom: '24px', 
                    borderBottom: '1px solid #eee' 
                  }}>
                    {post.featuredImage && (
                      <Link href={`/news/${post.slug}`} style={{ display: 'block', marginBottom: '12px' }}>
                        <img 
                          src={post.featuredImage} 
                          alt={post.title}
                          style={{ 
                            width: '100%', 
                            maxHeight: '200px', 
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      </Link>
                    )}
                    <h3 style={{ marginBottom: '8px' }}>
                      <Link href={`/news/${post.slug}`} style={{ 
                        color: '#1D2A5D', 
                        textDecoration: 'none',
                        fontSize: '18px',
                        fontWeight: 600
                      }}>
                        {post.title}
                      </Link>
                    </h3>
                    <div style={{ 
                      color: '#999', 
                      fontSize: '12px', 
                      marginBottom: '8px',
                      display: 'flex',
                      gap: '16px'
                    }}>
                      {post.category?.name && (
                        <span style={{ color: '#1D2A5D' }}>
                          {post.category.name}
                        </span>
                      )}
                      <span>{post.author?.username || '管理员'}</span>
                      <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                      <span>{post.viewCount || 0} 浏览</span>
                    </div>
                    <p style={{ 
                      color: '#666', 
                      fontSize: '14px',
                      lineHeight: 1.6,
                      marginBottom: '12px'
                    }}>
                      {stripHtml(post.excerpt || post.content, 200)}
                    </p>
                    <Link 
                      href={`/news/${post.slug}`}
                      style={{
                        color: '#1D2A5D',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      阅读更多 →
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">暂无新闻文章</p>
              </div>
            )}
          </div>
        }
      />
    </Layout>
  );
}

